import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import {
  iosNativeMarketingHero,
  iosNativePageRoot,
  iosNativeSolidBrandTitle,
} from '../styles/iosNativePageCss';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useAWSAuth as useAuth } from '../contexts/AWSAuthContext';
import { Sparkles, Camera, Leaf, TrendingUp, Heart, LogIn, UserPlus } from 'lucide-react';
import { useLatestScan } from '../hooks/useLatestScan';

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 50%);

  ${iosNativePageRoot}
`;

const HeroSection = styled.section`
  max-width: 1280px;
  margin: 0 auto;
  padding: 4rem 1rem;
  text-align: center;

  @media (min-width: ${theme.breakpoints.md}) {
    padding: 6rem 2rem;
  }

  ${iosNativeMarketingHero}
`;

const BrandTitle = styled.h1`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  background: linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.mint[500]} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 auto 0.75rem;

  ${iosNativeSolidBrandTitle}
  text-align: center;
  animation: slideUp 0.6s ease-out;

  @media (min-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize['4xl']};
  }
`;

const HeroTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
  animation: slideUp 0.6s ease-out 0.1s both;

  @media (min-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize['5xl']};
  }

  .highlight {
    background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.mint[500]} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const AppTagline = styled.p`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};
  color: #000000 !important;
  max-width: 800px;
  margin: 0 auto 1.5rem;
  line-height: 1.6;
  padding: 0 1rem;
  animation: slideUp 0.6s ease-out 0.05s both;

  @media (min-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.lg};
    padding: 0 2rem;
  }

  @media (min-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.xl};
    max-width: 900px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: #000000 !important;
  max-width: 600px;
  margin: 0 auto 2rem;
  line-height: 1.7;
  animation: slideUp 0.6s ease-out 0.1s both;

  @media (min-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.xl};
  }
`;

const CTAButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 400px;
  margin: 0 auto 3rem;
  animation: slideUp 0.6s ease-out 0.2s both;

  @media (min-width: ${theme.breakpoints.sm}) {
    flex-direction: row;
    max-width: none;
    justify-content: center;
  }
`;

const TaglineSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-top: 3rem;
  margin-bottom: 4rem;
  /* Removed fadeIn animation to prevent blurriness */
  opacity: 1;
  filter: none;
  transform: translateZ(0);
  backface-visibility: hidden;
  will-change: auto;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

const Tagline = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: white;
  border-radius: ${theme.borderRadius.full};
  box-shadow: ${theme.shadows.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  font-weight: ${theme.typography.fontWeight.medium};
  opacity: 1;
  filter: none;
  transform: translateZ(0);
  backface-visibility: hidden;
  will-change: auto;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  svg {
    width: 18px;
    height: 18px;
    color: ${theme.colors.primary[500]};
    opacity: 1;
    filter: none;
    transform: translateZ(0);
    backface-visibility: hidden;
    will-change: auto;
  }
`;

const FeaturesGrid = styled.div<{ $signedIn?: boolean }>`
  max-width: 1280px;
  margin: 0 auto;
  padding: 2rem 1rem 4rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  transform: ${props => props.$signedIn ? 'translateY(-20%)' : 'none'};

  @media (min-width: ${theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(4, 1fr);
    padding: 2rem 2rem 6rem;
  }
`;

const FeatureCard = styled(Card)<{ delay?: string }>`
  text-align: center;
  animation: slideUp 0.5s ease-out both;
  animation-delay: ${(props) => props.delay || '0s'};
`;

const LatestResultCard = styled(Card)`
  max-width: 640px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  align-items: flex-start;

  @media (min-width: ${theme.breakpoints.sm}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const LatestResultMeta = styled.div`
  display: grid;
  gap: 0.35rem;

  h3 {
    margin: 0;
    font-size: ${theme.typography.fontSize.xl};
    font-family: ${theme.typography.fontFamily.secondary};
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }

  p {
    margin: 0;
    color: ${theme.colors.text.secondary};
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const IconWrapper = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.glow.mint};
  border-radius: ${theme.borderRadius.xl};
  color: ${theme.colors.primary[600]};

  svg {
    width: 32px;
    height: 32px;
  }
`;

const FeatureTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const FeatureDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
`;

const features = [
  {
    icon: Camera,
    title: 'AI Face Scan',
    description: 'Advanced skin analysis powered by AI to identify your unique skin concerns',
  },
  {
    icon: Leaf,
    title: 'Natural DIY Masks',
    description: 'Science-backed recipes using natural ingredients for healthy, glowing skin',
  },
  {
    icon: TrendingUp,
    title: 'Track Your Glow',
    description: 'Monitor your progress with GlowScore™ and see real improvements over time',
  },
  {
    icon: Heart,
    title: 'Holistic Wellness',
    description: 'Diet tips, hydration tracking, and lifestyle coaching for radiant skin',
  },
];

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { latestScan, loading: latestLoading } = useLatestScan();
  const taglineSectionRef = useRef<HTMLDivElement>(null);


  const latestResultLink =
    latestScan && latestScan.status === 'complete'
      ? `/results/${latestScan.scanId}`
      : null;
  const latestResultDate =
    latestScan && (latestScan.createdAt || latestScan.timestamp)
      ? (() => {
          const date = new Date(latestScan.createdAt || latestScan.timestamp);
          if (Number.isNaN(date.getTime())) {
            return null;
          }
          return date.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          });
        })()
      : null;

  return (
    <PageContainer>
      <HeroSection>
        <BrandTitle>GlowLens™</BrandTitle>
        <AppTagline>
          GlowLens transforms your daily skincare routine into a personalized health system — guiding your diet, products, and habits with real-time AI feedback for clear, healthy skin.
        </AppTagline>
        <HeroTitle>
          Let's glow <span className="highlight">smarter</span>,<br />not harder ✨
        </HeroTitle>
        <HeroSubtitle>
          AI-powered skincare analysis + natural wellness for your best skin ever. No filters. Real skin. Real glow.
        </HeroSubtitle>
        
        <CTAButtons>
          {user ? (
            <>
              <Button
                size="lg"
                onClick={() => navigate('/scan')}
              >
                <Camera size={20} />
                Start Skin Scan
              </Button>
              <Button
                size="lg"
                variant="secondary"
                onClick={() => navigate('/profile')}
              >
                View Profile
              </Button>
            </>
          ) : (
            <>
              <Button
                size="lg"
                onClick={() => navigate('/signup')}
              >
                <UserPlus size={20} />
                Get Started
              </Button>
              <Button
                size="lg"
                variant="secondary"
                onClick={() => navigate('/signin')}
              >
                <LogIn size={20} />
                Sign In
              </Button>
            </>
          )}
        </CTAButtons>

        {user && (
          <LatestResultCard hover padding="lg">
            <LatestResultMeta>
              <h3>
                <Sparkles size={20} />
                Latest Result
              </h3>
              {latestLoading ? (
                <p>Loading your latest scan…</p>
              ) : latestResultLink ? (
                <>
                  <p>
                    Skin Health Index™: <strong>{latestScan?.glowScore ?? '--'}</strong>
                  </p>
                  {latestResultDate && <p>Completed on {latestResultDate}</p>}
                </>
              ) : latestScan ? (
                <p>Your latest scan is still {latestScan.status}. We'll update this card once analysis completes.</p>
              ) : (
                <p>No scans yet — upload front & side photos to get started.</p>
              )}
            </LatestResultMeta>
            <Button
              variant={latestResultLink ? 'secondary' : 'primary'}
              onClick={() => navigate(latestResultLink ?? (latestScan ? `/results/${latestScan.scanId}` : '/scan'))}
              disabled={latestLoading}
            >
              {latestResultLink ? 'View latest result' : latestScan ? 'View scan status' : 'Upload photos'}
            </Button>
          </LatestResultCard>
        )}

        <TaglineSection ref={taglineSectionRef} data-tagline-section>
          <Tagline>
            <Sparkles />
            No filters needed
          </Tagline>
          <Tagline>
            <Heart />
            Gen-Z wellness
          </Tagline>
          <Tagline>
            <Leaf />
            Natural ingredients
          </Tagline>
        </TaglineSection>
      </HeroSection>

      <FeaturesGrid $signedIn={!!user}>
        {features.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <FeatureCard 
              key={feature.title} 
              hover 
              padding="lg"
              delay={`${0.4 + index * 0.1}s`}
            >
              <IconWrapper>
                <Icon />
              </IconWrapper>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          );
        })}
      </FeaturesGrid>
    </PageContainer>
  );
};

export default HomePage;
